import { PostProcess } from '@babylonjs/core/PostProcesses/postProcess';
import { PassPostProcess } from '@babylonjs/core/PostProcesses/passPostProcess';
import { Constants } from '@babylonjs/core/Engines/constants';
import { Effect } from '@babylonjs/core/Materials/effect';

export default {
    methods: {
        configurePostProcess() {
            let hasTaa = false;

            if (this.controller.blueprint.custom['three-use-progressive-shadows'] === 'true') {
                this.useProgressiveShadows = true;
                hasTaa = true;
            }

            if (this.controller.blueprint.custom['three-use-progressive-aa'] === 'true') {
                this.useProgressiveAa = true;
                hasTaa = true;
            }

            if (hasTaa) {
                this.configureTaa();
            }
        },

        resetTaa() {
            this.taaSamplingStep = 0;
            this.taaEnabled = false;

            this.taaWaitForRender = true;

            // Use temporary darkness values.
            if (this.useProgressiveShadows || this.useProgressiveAa) {
                if (this.lights) {
                    Object.values(this.lights).forEach((l) => {
                        if (l.shadowDarkness) {
                            if (l.shadowGenerator) {
                                l.shadowGenerator.darkness = l.shadowDarknessPaused;
                            }
                        }
                    });
                }
            }
        },

        afterRenderTaa() {
            if (this.taaWaitForRender) {
                this.taaWaitForRender = false;

                // Enable praa after 200ms the camera movement ended.
                if (this.taaDelayTimer != null) {
                    clearTimeout(this.taaDelayTimer);
                }
                this.taaDelayTimer = setTimeout(() => {
                    this.enableTaa();
                }, 200);
            }
        },

        enableTaa() {
            Object.values(this.lights).forEach((l) => {
                if (l.shadowDarkness) {
                    if (l.shadowGenerator) {
                        l.shadowGenerator.darkness = l.shadowDarkness;
                    }
                }
            });

            this.scene.render();

            this.captureStaticPoster();

            this.taaEnabled = true;

            if (this.controller && this.controller.blueprint && this.controller.blueprint.custom) {
                if (this.controller.blueprint.custom['three-progressive-catchup']) {
                    const count = this.controller.blueprint.custom['three-progressive-catchup'];

                    for (let i = 0; i < count; i += 1) {
                        this.scene.render();
                    }
                }
            }
        },

        configureTaa() {
            this.taaSamplingStep = 0;
            this.taaEnabled = false;

            this.resetTaa();

            // Shader.
            Effect.ShadersStore.praaCombineFragmentShader = `
                precision highp float;

                varying vec2 vUV;
                uniform float amount;
                uniform sampler2D textureSampler;
                uniform sampler2D lastFrameSampler;

                void main(void)
                {
                    vec4 current = texture2D(textureSampler, vUV);
                    vec4 last = texture2D(lastFrameSampler, vUV);

                    gl_FragColor = last * (1.0 - amount) + current * amount;
                }`;

            // eslint-disable-next-line
            const praaCombinePass = new PostProcess('praaCombinePass', 'praaCombine', ['amount'], ['lastFrameSampler'], 1, this.camera, Constants.TEXTURE_NEAREST_NEAREST);

            let lastFramePass;

            praaCombinePass.onApplyObservable.add((pEffect) => {
                if (this.taaEnabled && this.taaSamplingStep > 0) {
                    pEffect.setTextureFromPostProcess('lastFrameSampler', lastFramePass);

                    const tAmount = 1.0 / (this.taaSamplingStep + 1);
                    pEffect.setFloat('amount', tAmount);
                } else {
                    pEffect.setTextureFromPostProcess('lastFrameSampler', praaCombinePass);
                    pEffect.setFloat('amount', 1.0);
                }
            });

            lastFramePass = new PassPostProcess('lastFramePass', 1, this.camera, Constants.TEXTURE_NEAREST_NEAREST, undefined, true);

            if (this.backgroundLayer) {
                lastFramePass.alphaMode = Constants.ALPHA_COMBINE;
            }

            this.camera.onViewMatrixChangedObservable.add(() => {
                this.resetTaa();
            });

            this.scene.onBeforeRenderObservable.add(() => {
                if (this.taaEnabled) {
                    if (this.useProgressiveAa) {
                        // Modify projection matrix to add random sub-pixel jitter.
                        const projectionMatrix = this.camera.getProjectionMatrix(true);

                        (projectionMatrix.m)[8] = ((Math.random() * 2) - 1.0) / this.engine.getRenderWidth();
                        (projectionMatrix.m)[9] = ((Math.random() * 2) - 1.0) / this.engine.getRenderHeight();

                        this.camera.freezeProjectionMatrix(projectionMatrix);
                    }

                    if (this.useProgressiveShadows) {
                        Object.values(this.lights).forEach((l) => {
                            // Jiggle position.
                            if (l.type === 'spot' && l.shadowPositionRange) {
                                if (l.originalPosition == null) {
                                    l.originalPosition = l.light.position.clone();
                                }

                                const position = l.originalPosition.clone();

                                const d = l.shadowPositionRange;

                                position.x += (Math.random() * d.x) - (d.x * 0.5);
                                position.y += (Math.random() * d.y) - (d.y * 0.5);
                                position.z += (Math.random() * d.z) - (d.z * 0.5);

                                l.light.position.copyFrom(position);
                            }

                            // Jiggle target.
                            if (l.type === 'spot' && l.shadowTargetRange) {
                                if (l.originalDirection == null) {
                                    l.originalDirection = l.light.direction.clone();
                                }

                                const target = l.target.clone();

                                const d = l.shadowTargetRange;

                                target.x += (Math.random() * d.x) - (d.x * 0.5);
                                target.y += (Math.random() * d.y) - (d.y * 0.5);
                                target.z += (Math.random() * d.z) - (d.z * 0.5);

                                const direction = target.subtract(l.light.position);
                                if (direction.lengthSquared > 0) {
                                    direction.normalize();
                                }

                                l.light.direction.copyFrom(direction);
                            }
                        });
                    }
                }
            });

            this.scene.onAfterRenderObservable.add(() => {
                if (this.taaEnabled) {
                    this.taaSamplingStep += 1;

                    // Fade out poster.
                    let steps = 64;

                    if (this.controller.blueprint.custom['three-max-progressive-steps']) {
                        steps = +this.controller.blueprint.custom['three-max-progressive-steps'];
                    }

                    if (this.taaSamplingStep > steps) {
                        this.removeStaticPoster(500);

                        // this.taaEnabled = false;
                    }
                }

                // Reset projection matrix and light direction.
                this.camera.unfreezeProjectionMatrix();

                Object.values(this.lights).forEach((l) => {
                    if (l.type === 'spot' && l.shadowPositionRange) {
                        if (l.originalPosition != null) {
                            l.light.position.copyFrom(l.originalPosition);
                        }
                    }

                    if (l.type === 'spot' && l.shadowTargetRange) {
                        if (l.originalDirection != null) {
                            l.light.direction.copyFrom(l.originalDirection);
                        }
                    }
                });
            });
        },
    },
};
